import { PublicRoutes } from "@/constants/routes";

/**
 * Return-to-URL after login. A protected route sends the visitor to
 * `/auth?redirect=<path>`; the login page keeps that path in sessionStorage
 * (the OTP flow walks through other /auth URLs and would lose the query), and
 * the successful login navigates back to it.
 *
 * Only same-origin relative paths are ever accepted — anything else
 * (`//evil.com`, `https://…`, `/\evil.com`, `javascript:`) is dropped, so the
 * parameter can't be turned into an open redirect.
 */
export const LOGIN_REDIRECT_PARAM = "redirect";

const STORAGE_KEY = "fidexa:login-redirect";

/** Placeholder origin: only used to let the URL parser normalize the path. */
const BASE = "https://app.invalid";

type PathParts = { pathname: string; search?: string; hash?: string };

/** The safe relative path, or `null` when it can't be trusted. */
export const sanitizeRedirectPath = (
  value: string | null | undefined
): string | null => {
  if (typeof value !== "string") return null;
  const candidate = value.trim();

  // Must be an absolute path on this site: one leading slash, no
  // protocol-relative "//", no backslash (browsers read "/\" as "//"), and no
  // control characters (a tab or newline inside "/\t/evil.com" is stripped
  // by the parser and becomes "//evil.com").
  if (!candidate.startsWith("/") || candidate.startsWith("//")) return null;
  if (candidate.includes("\\")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(candidate)) return null;

  let url: URL;
  try {
    url = new URL(candidate, BASE);
  } catch {
    return null;
  }
  if (url.origin !== BASE) return null;

  const path = `${url.pathname}${url.search}${url.hash}`;
  // Going back to the login itself would loop.
  if (
    url.pathname === PublicRoutes.AUTH ||
    url.pathname.startsWith(`${PublicRoutes.AUTH}/`)
  ) {
    return null;
  }
  return path;
};

/**
 * Login URL that brings the visitor back to `location` afterwards. The site
 * root carries no redirect: after login the dashboard is already the default.
 */
export const buildLoginPath = ({
  pathname,
  search = "",
  hash = "",
}: PathParts): string => {
  const path = sanitizeRedirectPath(`${pathname}${search}${hash}`);
  if (!path || path === "/") return PublicRoutes.AUTH;
  return `${PublicRoutes.AUTH}?${LOGIN_REDIRECT_PARAM}=${encodeURIComponent(path)}`;
};

/**
 * Keeps a valid redirect for the end of the login flow. A missing or invalid
 * one clears what was kept, so an abandoned visit doesn't steer a later login.
 */
export const rememberLoginRedirect = (value: string | null | undefined) => {
  const path = sanitizeRedirectPath(value);
  if (!path) {
    clearLoginRedirect();
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    // Private mode / blocked storage: the login just falls back to /dashboard.
  }
};

export const clearLoginRedirect = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same as above: nothing to clear when storage is unavailable.
  }
};

/** Reads and forgets the stored redirect (validated again on the way out). */
export const consumeLoginRedirect = (): string | null => {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  clearLoginRedirect();
  return sanitizeRedirectPath(stored);
};
